/**
 * League view: games for a single league grouped by date using FlashList.
 * Sticky date headers and performant virtualization.
 * League banner taps open league detail when leagueId is on match data.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import { useRouter } from 'expo-router';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn';
import { useTheme } from '../../../src/theme/useTheme';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { getLeagueSectionByDateStyles } from './LeagueSectionByDate.styles';
import { ScoreboardMatchRow } from '../ScoreboardMatchRow/ScoreboardMatchRow';
import { buildLeagueDetailHref } from '../../../src/gameResults/helpers/buildLeagueDetailHref.helper';
import { groupGamesByDate } from '../../../src/gameResults/helpers/groupGamesByDate.helper';
import { buildLeagueDateFlatList } from '../../../src/gameResults/helpers/leagueGamesFlatListBuilder.helper';
import type { GameResult } from '../../../src/gameResults/types/gameResults.types';
import type { LeagueDateListItem } from '../../../src/gameResults/types/gameResults.types';

interface LeagueSectionByDateProps {
  games: GameResult[];
  testID?: string;
}

const ITEM_TYPE_HEADER = 'dateHeader';
const ITEM_TYPE_GAME = 'game';
const ITEM_TYPE_FOOTER = 'sectionFooter';

export const LeagueSectionByDate: React.FC<LeagueSectionByDateProps> = ({
  games,
  testID,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const styles = useMemo(() => getLeagueSectionByDateStyles(theme), [theme]);
  const leagueBannerName = games[0]?.league?.trim() ?? '';
  const leagueBannerCode = games[0]?.leagueCountryCode;
  const leagueId = games[0]?.leagueId;

  const handleLeagueBannerPress = useCallback(() => {
    if (leagueId === undefined || leagueId === '' || leagueBannerName === '') return;
    const code = games[0]?.leagueCountryCode;
    router.push(buildLeagueDetailHref(leagueBannerName, leagueId, code));
  }, [games, leagueBannerName, leagueId, router]);

  const openMatchHub = useCallback(
    (gameId: string) => {
      router.push({
        pathname: '/app/match/[gameId]',
        params: { gameId, returnTo: '/app/gameResults' },
      });
    },
    [router],
  );
  const { data, stickyHeaderIndices } = useMemo(() => {
    const sections = groupGamesByDate(games);
    return buildLeagueDateFlatList(sections);
  }, [games]);

  const getItemType = useCallback((item: LeagueDateListItem) => {
    if (item.type === 'dateHeader') return ITEM_TYPE_HEADER;
    if (item.type === 'sectionFooter') return ITEM_TYPE_FOOTER;
    return ITEM_TYPE_GAME;
  }, []);

  const keyExtractor = useCallback(
    (item: LeagueDateListItem) => {
      if (item.type === 'dateHeader') return `header-${item.dateKey}`;
      if (item.type === 'sectionFooter') return `footer-${item.dateKey}`;
      return item.game.id;
    },
    []
  );

  const renderItem = useCallback(
    ({ item, index: listIndex }: ListRenderItemInfo<LeagueDateListItem>) => {
      if (item.type === 'dateHeader') {
        return (
          <StaggeredFadeIn index={listIndex}>
            <View style={styles.dateHeader}>
              <Text style={styles.dateHeaderText}>{item.title}</Text>
            </View>
          </StaggeredFadeIn>
        );
      }
      if (item.type === 'sectionFooter') {
        return <View style={styles.sectionFooter} />;
      }
      return (
        <StaggeredFadeIn index={listIndex}>
          <View style={styles.card}>
            <ScoreboardMatchRow
              game={item.game}
              isLast={item.isLastInSection}
              testID={testID ? `${testID}-row-${item.game.id}` : undefined}
              onPress={(g) => {
                openMatchHub(g.id);
              }}
            />
          </View>
        </StaggeredFadeIn>
      );
    },
    [
      styles.dateHeader,
      styles.dateHeaderText,
      styles.card,
      styles.sectionFooter,
      testID,
      openMatchHub,
    ]
  );

  let leagueBannerBlock: React.ReactNode = null;
  if (leagueBannerName.length > 0) {
    const bannerInner = (
      <>
        {leagueBannerCode != null && leagueBannerCode.length > 0 && (
          <View style={styles.leagueBannerFlagWrap}>
            <CountryFlag
              countryCode={leagueBannerCode}
              size="compact"
              accessibilityLabel={`${leagueBannerName} country flag`}
            />
          </View>
        )}
        <Text style={styles.leagueBannerText} numberOfLines={1}>
          {leagueBannerName}
        </Text>
      </>
    );
    leagueBannerBlock =
      leagueId != null && leagueId !== '' ? (
        <Pressable
          style={({ pressed }) => [styles.leagueBanner, pressed && styles.leagueBannerPressed]}
          onPress={handleLeagueBannerPress}
          accessibilityRole="button"
          accessibilityLabel={`Open ${leagueBannerName} league`}
        >
          {bannerInner}
        </Pressable>
      ) : (
        <View style={styles.leagueBanner} accessibilityRole="header">
          {bannerInner}
        </View>
      );
  }

  return (
    <View style={styles.container}>
      {leagueBannerBlock}
      <FlashList<LeagueDateListItem>
        overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={data}
        getItemType={getItemType}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        stickyHeaderIndices={stickyHeaderIndices}
        testID={testID ?? 'league-section-by-date'}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};
